
import { Injectable } from "@angular/core";
import {
  Actions,
  createEffect,
  ofType
} from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
  switchMap,
  withLatestFrom
} from "rxjs/operators";

import {
  BusinessDateRange
} from "../model/business-dates/business-date-range.model";
import {
  PeriodOnPeriodType,
  TemporalAggregation
} from "../model/business-dates/temporal-aggregation.enum";
import {
  Timestamp
} from "../model/timestamp.model";
import {
  TemporalService
} from "../services/temporal/temporal.service";
import {
  ContextConfiguration
} from "./config/context.config";
import { Context } from "./context";
import {
  BUSINESS_DATE_RANGE_AND_TEMPORAL_AGGREGATION_CHANGED,
  BUSINESS_DATE_RANGE_CHANGED,
  BusinessDateRangeAndTemporalAggregationChangeComplete,
  BusinessDateRangeAndTemporalAggregationChanged,
  BusinessDateRangeChangeComplete,
  BusinessDateRangeChanged,
  CALENDAR_DATE_RANGE_AND_TEMPORAL_AGGREGATION_CHANGED,
  CalendarDateRangeAndTemporalAggregationChangeComplete,
  CalendarDateRangeAndTemporalAggregationChanged,
  CurrentBusinessDateChangeComplete
} from "./temporal.actions";
import {
  AUTHENTICATION_COMPLETED
} from "./user.actions";
import { USER_RELOGIN } from "../util/string-constants"
import { _isActivityThresholdReached } from "./utils";
import { Noop } from "./effects";
import { UserContext } from "./user.reducers";

const _DEFAULT_TEMPORAL_AGGREGATION = TemporalAggregation.Week;
const _DEFAULT_BUSINESS_DATE_RANGE = {Last: {weeks: 0}, Next: {weeks: 0}};
const _DEFAULT_TEMPORAL_L4L = PeriodOnPeriodType.DEFAULT;

@Injectable()
export class TemporalEffects {

  constructor(private _action$: Actions, private _temporalService: TemporalService, private _store$: Store<Context>) {
  }

  // @Effect()
  public businessDateRangeAndTemporalAggregationChanged$ = createEffect(() => this._action$.pipe(
    ofType(BUSINESS_DATE_RANGE_AND_TEMPORAL_AGGREGATION_CHANGED),
    withLatestFrom(this._store$),
    switchMap(async ([action, context]) => {
      console.log("Business Date Range AND Temporal Aggregation changed.", action, context);
      const config = ContextConfiguration.LOADED;
      const businessDateRange: BusinessDateRange = (action as BusinessDateRangeAndTemporalAggregationChanged).businessDateRange
                                                    || context.temporal.range;
      let baselineBusinessDate = (action as BusinessDateRangeAndTemporalAggregationChanged).baselineBusinessDate || context.temporal.current;
      const temporalOffset = (action as BusinessDateRangeAndTemporalAggregationChanged).temporalOffset;
      if (null != temporalOffset) {
        baselineBusinessDate = await this._temporalService.businessDateOffset(
          baselineBusinessDate,
          temporalOffset.offset,
          temporalOffset.temporalAggregation,
          temporalOffset.direction
        );
      }

      const temporalAggregation = (action as BusinessDateRangeAndTemporalAggregationChanged).temporalAggregation || context.temporal.aggregation;
      const dateType = (action as BusinessDateRangeAndTemporalAggregationChanged).dateType || context.temporal.dateType;
      const convertToAbsolute = (action as BusinessDateRangeAndTemporalAggregationChanged).convertToAbsolute || false;

      const newRange = await this._temporalService.HydratedBusinessDateRanges(
        businessDateRange,
        config.temporalAggregations,
        baselineBusinessDate,
        convertToAbsolute
      );
      return new BusinessDateRangeAndTemporalAggregationChangeComplete(newRange, temporalAggregation, dateType);
    })
  ));

  // @Effect()
  public calendarDateRangeAndTemporalAggregationChanged$ = createEffect(() => this._action$
  .pipe(
    ofType(CALENDAR_DATE_RANGE_AND_TEMPORAL_AGGREGATION_CHANGED),
    withLatestFrom(this._store$),
    switchMap(async ([action, context]) => {
      const config = ContextConfiguration.LOADED;
      const timestamp: Timestamp = (action as CalendarDateRangeAndTemporalAggregationChanged).timestamp;

      const temporalAggregation = (action as CalendarDateRangeAndTemporalAggregationChanged).temporalAggregation || context.temporal.aggregation;
      const dateType = (action as CalendarDateRangeAndTemporalAggregationChanged).dateType || context.temporal.dateType;

      const newRange = await this._temporalService.HydratedCalendarTimestamp(timestamp,
        config.temporalAggregations);
      return new CalendarDateRangeAndTemporalAggregationChangeComplete(newRange, temporalAggregation, dateType);
    })
  ));

  // @Effect()
  public init$ = createEffect(() => this._action$
  .pipe(
    ofType(AUTHENTICATION_COMPLETED),
    withLatestFrom(this._store$),
    switchMap(async ([, context]) => {
      let rangeAndAggregation;
      const config = ContextConfiguration.LOADED;
      const newUserContext = context && context.user ? context.user : null;
      if (null != newUserContext && true === _isActivityThresholdReached(context.user)) {
        const userProfile = newUserContext.profile;
        if (userProfile != null) {
          rangeAndAggregation = {...userProfile.DefaultContext.temporal};
        } else {
          rangeAndAggregation = await this._getRangeAndAggregation(context);
        }
      } else {
        rangeAndAggregation = await this._getRangeAndAggregation(context);
      }
      const temporalContext = await this._temporalService.HydrateInitialBaselineDatesAndRanges(
        rangeAndAggregation.range,
        rangeAndAggregation.aggregation,
        config.temporalAggregations,
        rangeAndAggregation.L4L
      );
      return new CurrentBusinessDateChangeComplete(temporalContext);
    })
  ));


  // @Effect()
  public businessDateRangeChanged$ =
  createEffect(() => this._action$
  .pipe(
    ofType(BUSINESS_DATE_RANGE_CHANGED),
    withLatestFrom(this._store$),
    switchMap(async ([action, context]) => {
      const config = ContextConfiguration.LOADED;

      const newRange = (action as BusinessDateRangeChanged).payload;
      const newRanges = await this._temporalService.HydratedBusinessDateRanges(
        newRange,
        config.temporalAggregations,
        context.temporal.current
      );
      return new BusinessDateRangeChangeComplete(newRanges);
    })
  ));

  private config: ContextConfiguration;

  private async _getRangeAndAggregation(context: Context): Promise<{range: BusinessDateRange, aggregation: TemporalAggregation}> {

    const temporal = context.temporal;
    const ret: any = {
      range: temporal.range || context.user.profile.DefaultContext.temporal.range,
      aggregation: temporal.aggregation || context.user.profile.DefaultContext.temporal.aggregation,
      L4L: temporal.L4L || context.user.profile.DefaultContext.temporal.L4L
    };

    ret.range = ret.range || _DEFAULT_BUSINESS_DATE_RANGE;
    ret.aggregation = ret.aggregation || _DEFAULT_TEMPORAL_AGGREGATION;
    ret.L4L = ret.L4L || _DEFAULT_TEMPORAL_L4L;

    // console.log("Getting the range after login.", ret);
    return ret;
  }

}
